import React, { useEffect, useMemo, useState } from 'react';
import { apiErrorMessage, hl7RouteAPI, vpnAPI } from '../services/api';
import { Copy, FileText, Pencil, Plus, RefreshCw, Save, Shield, Trash2, Wand2, Wifi, Activity, TerminalSquare, Route, HelpCircle, PlayCircle, StopCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface HL7RouteStatus {
  service_running: boolean;
  tunnel_up: boolean;
  public_ip: string;
  public_host: string;
  forward_port: number;
  target_host: string;
  target_port: number;
  allowed_source_cidr: string;
  mirth_listener_open: boolean;
  nat_configured: boolean;
  ipsec_status: string;
}

interface SavedConnection {
  id: number;
  name: string;
  description?: string;
  site_label?: string;
  peer_address?: string;
  peer_id?: string;
  remote_subnet?: string;
  local_port: number;
  remote_port: number;
  protocol: string;
  device_profile?: string;
  ike_version?: string;
  proposal_profile?: string;
  local_protected_subnet?: string;
  remote_network_mode?: string;
  availability_mode?: string;
  alert_when_down?: boolean;
  expected_online_window?: string;
  psk_hint?: string;
  auto_start?: boolean;
  config_path?: string;
  is_active: boolean;
}

interface ConnectionDiagnostic {
  connection: SavedConnection;
  summary: {
    configured: boolean;
    established: boolean;
    connecting: boolean;
    expected_online?: boolean;
    standby_ok?: boolean;
    status_label?: string;
    route_ready: boolean;
    overall_ok: boolean;
  };
  checks: Array<{ name: string; ok: boolean; detail: string }>;
  next_steps?: Array<{ level: string; title: string; detail: string }>;
  recent_logs?: string[];
  public_endpoint: string;
  internal_endpoint: string;
  ipsec_status: string;
  ipsec_statusall_excerpt: string;
  timestamp: string;
}

interface ConnectionForm {
  id?: number;
  name: string;
  site_label: string;
  description: string;
  peer_address: string;
  peer_id: string;
  remote_subnet: string;
  local_port: number;
  remote_port: number;
  protocol: string;
  device_profile: string;
  ike_version: string;
  proposal_profile: string;
  local_protected_subnet: string;
  remote_network_mode: string;
  availability_mode: string;
  alert_when_down: boolean;
  expected_online_window: string;
  psk_hint: string;
  psk: string;
  auto_start: boolean;
}

const protectedDefault = '172.16.1.2/32';

const emptyForm: ConnectionForm = {
  name: '',
  site_label: '',
  description: '',
  peer_address: '',
  peer_id: '',
  remote_subnet: '192.168.1.100/32',
  local_port: 6661,
  remote_port: 6661,
  protocol: 'IKEv2/PSK',
  device_profile: 'unifi-route-based',
  ike_version: 'ikev2',
  proposal_profile: 'unifi-auto-compatible',
  local_protected_subnet: protectedDefault,
  remote_network_mode: 'single-sender',
  availability_mode: 'always_on',
  alert_when_down: true,
  expected_online_window: 'always',
  psk_hint: '',
  psk: '',
  auto_start: true,
};

const deviceProfiles = [
  {
    id: 'unifi-route-based',
    title: 'UniFi / UDM / UXG route-based',
    short: 'Best default for UniFi customer sites',
    ike_version: 'ikev2',
    proposal_profile: 'unifi-auto-compatible',
    remote_network_mode: 'single-sender',
  },
  {
    id: 'standard-ikev2',
    title: 'Standard IKEv2 firewall',
    short: 'Fortinet, SonicWall, pfSense, MikroTik, Cisco, etc.',
    ike_version: 'ikev2',
    proposal_profile: 'modern-strict',
    remote_network_mode: 'single-sender',
  },
  {
    id: 'legacy-ikev1',
    title: 'Legacy IKEv1 firewall',
    short: 'Older devices that cannot do IKEv2/SHA256 cleanly',
    ike_version: 'ikev1',
    proposal_profile: 'legacy-ikev1-compatible',
    remote_network_mode: 'site-subnet',
  },
  {
    id: 'unknown-troubleshooting',
    title: 'Unknown device / troubleshooting',
    short: 'Broad crypto while onboarding; tighten after it connects',
    ike_version: 'ikev2',
    proposal_profile: 'broad-troubleshooting',
    remote_network_mode: 'single-sender',
  },
];

const proposalLabels: Record<string, string> = {
  'modern-strict': 'Modern strict: AES-256/SHA256/DH14',
  'unifi-auto-compatible': 'UniFi/common: AES-256/SHA256 + AES-128/SHA1 fallback',
  'legacy-ikev1-compatible': 'Legacy IKEv1-compatible AES/SHA fallback set',
  'broad-troubleshooting': 'Broad troubleshooting; tighten later',
};

const availabilityLabels: Record<string, { title: string; short: string; auto: boolean; alert: boolean; window: string }> = {
  always_on: {
    title: 'Always on — alert if down',
    short: 'For production interfaces where HL7 may arrive anytime. VPS initiates and retries forever.',
    auto: true,
    alert: true,
    window: 'always',
  },
  customer_initiated: {
    title: 'Customer initiated — standby is normal',
    short: 'For batch/manual customers who turn VPN on only before sending HL7.',
    auto: false,
    alert: false,
    window: 'manual',
  },
};

function copy(text: string, label = 'Copied') {
  navigator.clipboard.writeText(text);
  toast.success(label);
}

function generatePSK() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-+=.@#%';
  const bytes = new Uint32Array(64);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (n) => chars[n % chars.length]).join('');
}

function formFromConnection(connection: SavedConnection): ConnectionForm {
  return {
    id: connection.id,
    name: connection.name,
    site_label: connection.site_label || '',
    description: connection.description || '',
    peer_address: connection.peer_address || '',
    peer_id: connection.peer_id || connection.peer_address || '',
    remote_subnet: connection.remote_subnet || '192.168.1.100/32',
    local_port: connection.local_port || 6661,
    remote_port: connection.remote_port || 6661,
    protocol: connection.protocol || 'IKEv2/PSK',
    device_profile: connection.device_profile || 'unifi-route-based',
    ike_version: connection.ike_version || 'ikev2',
    proposal_profile: connection.proposal_profile || 'unifi-auto-compatible',
    local_protected_subnet: connection.local_protected_subnet || protectedDefault,
    remote_network_mode: connection.remote_network_mode || 'single-sender',
    availability_mode: connection.availability_mode || (connection.auto_start === false ? 'customer_initiated' : 'always_on'),
    alert_when_down: connection.alert_when_down !== false,
    expected_online_window: connection.expected_online_window || (connection.auto_start === false ? 'manual' : 'always'),
    psk_hint: connection.psk_hint || '',
    psk: '',
    auto_start: connection.auto_start !== false,
  };
}

function normalizeName(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48);
}

export default function VPNConnections() {
  const [route, setRoute] = useState<HL7RouteStatus | null>(null);
  const [connections, setConnections] = useState<SavedConnection[]>([]);
  const [diagnostics, setDiagnostics] = useState<Record<number, ConnectionDiagnostic>>({});
  const [form, setForm] = useState<ConnectionForm>({ ...emptyForm });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [debugOpen, setDebugOpen] = useState<Record<number, boolean>>({});

  const load = async () => {
    try {
      setLoading(true);
      const [routeResponse, connectionsResponse] = await Promise.all([
        hl7RouteAPI.getStatus(),
        vpnAPI.getConnections(),
      ]);
      setRoute(routeResponse.data);
      const savedConnections: SavedConnection[] = connectionsResponse.data || [];
      setConnections(savedConnections);
      const diagnosticPairs = await Promise.all(
        savedConnections.map(async (connection) => {
          try {
            const response = await vpnAPI.getConnectionDiagnostics(connection.id);
            return [connection.id, response.data] as const;
          } catch {
            return [connection.id, null] as const;
          }
        })
      );
      setDiagnostics(Object.fromEntries(diagnosticPairs.filter(([, value]) => value)) as Record<number, ConnectionDiagnostic>);
    } catch (error: any) {
      toast.error('Failed to load VPN manager: ' + apiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const publicIp = route?.public_ip || '2.24.114.251';
  const protectedEndpoint = form.local_protected_subnet || protectedDefault;
  const protectedIpOnly = protectedEndpoint.split('/')[0];

  const applyProfile = (profileId: string) => {
    const profile = deviceProfiles.find((item) => item.id === profileId) || deviceProfiles[0];
    setForm({
      ...form,
      device_profile: profile.id,
      ike_version: profile.ike_version,
      proposal_profile: profile.proposal_profile,
      remote_network_mode: profile.remote_network_mode,
    });
  };

  const applyAvailability = (mode: string) => {
    const policy = availabilityLabels[mode] || availabilityLabels.always_on;
    setForm({
      ...form,
      availability_mode: mode,
      auto_start: policy.auto,
      alert_when_down: policy.alert,
      expected_online_window: policy.window,
    });
  };

  const generatedConfig = useMemo(() => {
    const peerAddress = form.peer_address || 'CUSTOMER_PUBLIC_IP_OR_DDNS';
    const peerId = form.peer_id || form.peer_address || 'CUSTOMER_PEER_ID';
    const remoteSubnet = form.remote_subnet || 'CUSTOMER_SENDER_32_OR_SITE_SUBNET';
    const proposals = proposalLabels[form.proposal_profile] || proposalLabels['unifi-auto-compatible'];
    const isAlwaysOn = form.availability_mode !== 'customer_initiated';
    return `conn ${form.name || 'customer-site-name'}
    keyexchange=${form.ike_version || 'ikev2'}
    authby=psk
    type=tunnel
    auto=${isAlwaysOn ? 'start' : 'add'}

    left=%defaultroute
    leftid=${publicIp}
    leftsubnet=${protectedEndpoint}
    leftauth=psk

    right=${peerAddress}
    rightid=${peerId}
    rightsubnet=${remoteSubnet}
    rightauth=psk

    # ${proposals}
    proposal_profile=${form.proposal_profile}
    dpdaction=${isAlwaysOn ? 'restart' : 'clear'}
    dpddelay=30s
    dpdtimeout=120s
    keyingtries=${isAlwaysOn ? '%forever' : '1'}`;
  }, [form, protectedEndpoint, publicIp]);

  const generatedSecret = useMemo(() => {
    const peerId = form.peer_id || form.peer_address || 'CUSTOMER_PEER_ID';
    return `${publicIp} ${peerId} : PSK "${form.psk || 'WRITE_ONLY_PSK'}"`;
  }, [form.peer_id, form.peer_address, form.psk, publicIp]);

  const customerInstructions = useMemo(() => {
    const customerSubnet = form.remote_subnet || 'their HL7 sender /32 or LAN subnet';
    return [
      `VPN gateway / remote peer: ${publicIp}`,
      `Pre-shared key: use the same PSK saved here`,
      `Customer/local protected network: ${customerSubnet}`,
      `VPS/remote protected network: ${protectedEndpoint}`,
      `HL7 destination after tunnel is up: ${protectedIpOnly}:${form.local_port || 6661}`,
      `Tunnel availability: ${availabilityLabels[form.availability_mode]?.title || form.availability_mode}`,
      `IKE version: ${(form.ike_version || 'ikev2').toUpperCase()}`,
      `Crypto profile: ${proposalLabels[form.proposal_profile] || form.proposal_profile}`,
    ];
  }, [form.remote_subnet, form.local_port, form.ike_version, form.proposal_profile, form.availability_mode, protectedEndpoint, protectedIpOnly, publicIp]);

  const resetForm = () => setForm({ ...emptyForm });

  const saveConnection = async () => {
    if (!form.name.trim()) {
      toast.error('Site name is required');
      return;
    }
    if (!form.peer_address.trim()) {
      toast.error('Customer public IP or DDNS is required');
      return;
    }
    if (!form.remote_subnet.trim()) {
      toast.error('Customer sender/subnet is required');
      return;
    }
    if (!form.id && !form.psk.trim()) {
      toast.error('PSK is required for a new connection');
      return;
    }
    try {
      setSaving(true);
      const payload: any = {
        name: form.name.trim(),
        site_label: form.site_label.trim() || form.name.trim(),
        description: form.description.trim(),
        peer_address: form.peer_address.trim(),
        peer_id: (form.peer_id || form.peer_address).trim(),
        remote_subnet: form.remote_subnet.trim(),
        local_port: Number(form.local_port || 6661),
        remote_port: Number(form.remote_port || form.local_port || 6661),
        protocol: form.protocol,
        device_profile: form.device_profile,
        ike_version: form.ike_version,
        proposal_profile: form.proposal_profile,
        local_protected_subnet: form.local_protected_subnet || protectedDefault,
        remote_network_mode: form.remote_network_mode,
        availability_mode: form.availability_mode,
        alert_when_down: form.alert_when_down,
        expected_online_window: form.expected_online_window,
        psk_hint: form.psk_hint.trim(),
        auto_start: form.auto_start,
      };
      if (form.psk.trim()) payload.psk = form.psk.trim();
      if (form.id) {
        await vpnAPI.updateConnection(form.id, payload);
        toast.success('Connection updated, config/secrets reloaded');
      } else {
        await vpnAPI.createConnection(payload);
        toast.success('Connection created, config/secrets loaded');
      }
      resetForm();
      await load();
    } catch (error: any) {
      toast.error('Save failed: ' + apiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteConnection = async (connection: SavedConnection) => {
    if (!window.confirm(`Delete VPN connection ${connection.name}? This removes its managed config and PSK block.`)) return;
    try {
      await vpnAPI.deleteConnection(connection.id);
      toast.success('Connection deleted');
      if (form.id === connection.id) resetForm();
      await load();
    } catch (error: any) {
      toast.error('Delete failed: ' + apiErrorMessage(error));
    }
  };

  const startConnection = async (connection: SavedConnection) => {
    try {
      await vpnAPI.startConnection(connection.id);
      toast.success(`Started ${connection.name}`);
      await load();
    } catch (error: any) {
      toast.error('Start failed: ' + apiErrorMessage(error));
    }
  };

  const stopConnection = async (connection: SavedConnection) => {
    if (!window.confirm(`Stop VPN connection ${connection.name}?`)) return;
    try {
      await vpnAPI.stopConnection(connection.id);
      toast.success(`Stopped ${connection.name}`);
      await load();
    } catch (error: any) {
      toast.error('Stop failed: ' + apiErrorMessage(error));
    }
  };

  if (loading || !route) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  const activeCount = Object.values(diagnostics).filter((item) => item.summary?.established).length;

  return (
    <div className="space-y-8">
      <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8 text-white shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100 ring-1 ring-white/15">
              <Shield className="mr-2 h-4 w-4" /> HL7 site-to-site VPN manager
            </div>
            <h1 className="text-3xl font-bold">Connect a customer VPN in minutes</h1>
            <p className="mt-2 max-w-3xl text-blue-100">
              Pick the customer firewall type, enter the public IP + sender subnet, paste/generate a PSK, then use the live diagnostics while the customer saves their side.
            </p>
          </div>
          <button onClick={load} className="inline-flex items-center rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-blue-50">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh all
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/15"><div className="text-sm text-blue-100">VPN node</div><div className="mt-1 text-xl font-bold">{route.service_running ? 'Running' : 'Down'}</div></div>
          <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/15"><div className="text-sm text-blue-100">HL7 route</div><div className="mt-1 text-xl font-bold">{route.nat_configured && route.mirth_listener_open ? 'Ready' : 'Blocked'}</div></div>
          <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/15"><div className="text-sm text-blue-100">Active tunnels</div><div className="mt-1 text-xl font-bold">{activeCount}/{connections.length}</div></div>
          <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/15"><div className="text-sm text-blue-100">Customers send to</div><div className="mt-1 font-mono text-lg font-bold">{protectedIpOnly}:{route.forward_port}</div></div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200 xl:col-span-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{form.id ? 'Edit customer VPN' : 'Add customer VPN'}</h2>
              <p className="mt-1 text-sm text-gray-600">Only four fields are usually required: site, device type, customer IP, sender subnet, and PSK.</p>
            </div>
            {form.id && <button onClick={resetForm} className="text-sm font-medium text-blue-600 hover:text-blue-700"><Plus className="mr-1 inline h-4 w-4" /> New</button>}
          </div>

          <div className="mt-6 space-y-6">
            <section>
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-700">1</span> Customer/site</div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="siteLabel">Customer/site name</label>
                  <input id="siteLabel" type="text" value={form.site_label} onChange={(e) => setForm({ ...form, site_label: e.target.value, name: form.name || normalizeName(e.target.value) })} placeholder="Acme Clinic - Main Office" />
                </div>
                <div>
                  <label htmlFor="name">Internal connection ID</label>
                  <input id="name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: normalizeName(e.target.value) || e.target.value })} placeholder="acme-main-hl7" />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="description">Notes</label>
                <input id="description" type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Contact, firewall model, ticket, or purpose" />
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-700">2</span> Customer firewall type</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {deviceProfiles.map((profile) => (
                  <button key={profile.id} type="button" onClick={() => applyProfile(profile.id)} className={`rounded-2xl border p-4 text-left transition ${form.device_profile === profile.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                    <div className="font-semibold text-gray-900">{profile.title}</div>
                    <div className="mt-1 text-sm text-gray-600">{profile.short}</div>
                  </button>
                ))}
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-700">3</span> Customer VPN details</div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="peerAddress">Customer public IP / DDNS</label>
                  <input id="peerAddress" type="text" value={form.peer_address} onChange={(e) => setForm({ ...form, peer_address: e.target.value, peer_id: form.peer_id || e.target.value })} placeholder="75.183.215.9 or vpn.customer.com" />
                  <p className="mt-1 text-xs text-gray-500">This is the customer firewall WAN IP or DNS name.</p>
                </div>
                <div>
                  <label htmlFor="remoteSubnet">HL7 sender or customer LAN</label>
                  <input id="remoteSubnet" type="text" value={form.remote_subnet} onChange={(e) => setForm({ ...form, remote_subnet: e.target.value })} placeholder="192.168.1.179/32 or 192.168.1.0/24" />
                  <p className="mt-1 text-xs text-gray-500">Use /32 for one sender. Use /24 only if the whole LAN should reach the route.</p>
                </div>
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-700">4</span> Tunnel availability</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {Object.entries(availabilityLabels).map(([mode, policy]) => (
                  <button key={mode} type="button" onClick={() => applyAvailability(mode)} className={`rounded-2xl border p-4 text-left transition ${form.availability_mode === mode ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                    <div className="font-semibold text-gray-900">{policy.title}</div>
                    <div className="mt-1 text-sm text-gray-600">{policy.short}</div>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Always-on tunnels are treated like production outages when down. Customer-initiated tunnels can be off without creating an alert; the VPS waits for them.
              </p>
            </section>

            <section>
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-700">5</span> Shared secret</div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="psk">Pre-shared key {form.id ? '(paste only to rotate/update)' : ''}</label>
                  <div className="flex gap-2">
                    <input id="psk" type="text" value={form.psk} onChange={(e) => setForm({ ...form, psk: e.target.value })} placeholder={form.id ? 'Leave blank to keep current PSK' : 'Generate or paste PSK'} />
                    <button type="button" onClick={() => setForm({ ...form, psk: generatePSK() })} className="inline-flex items-center rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700">
                      <Wand2 className="mr-2 h-4 w-4" /> Generate
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Write-only: saved to StrongSwan, never returned by the API.</p>
                </div>
                <div>
                  <label htmlFor="pskHint">PSK storage note</label>
                  <input id="pskHint" type="text" value={form.psk_hint} onChange={(e) => setForm({ ...form, psk_hint: e.target.value })} placeholder="Bitwarden item / rotated 2026-06" />
                </div>
              </div>
            </section>

            <details open={showAdvanced} onToggle={(e) => setShowAdvanced((e.target as HTMLDetailsElement).open)} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <summary className="cursor-pointer font-semibold text-gray-900">Advanced IPsec settings</summary>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label htmlFor="ikeVersion">IKE version</label>
                  <select id="ikeVersion" value={form.ike_version} onChange={(e) => setForm({ ...form, ike_version: e.target.value })}>
                    <option value="ikev2">IKEv2</option>
                    <option value="ikev1">IKEv1</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="proposalProfile">Crypto/proposal profile</label>
                  <select id="proposalProfile" value={form.proposal_profile} onChange={(e) => setForm({ ...form, proposal_profile: e.target.value })}>
                    {Object.entries(proposalLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="networkMode">Customer network mode</label>
                  <select id="networkMode" value={form.remote_network_mode} onChange={(e) => setForm({ ...form, remote_network_mode: e.target.value })}>
                    <option value="single-sender">Single HL7 sender /32</option>
                    <option value="site-subnet">Whole customer site subnet</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="peerId">Customer peer ID</label>
                  <input id="peerId" type="text" value={form.peer_id} onChange={(e) => setForm({ ...form, peer_id: e.target.value })} placeholder="Usually same as public IP/DDNS" />
                </div>
                <div>
                  <label htmlFor="protectedSubnet">VPS protected network</label>
                  <input id="protectedSubnet" type="text" value={form.local_protected_subnet} onChange={(e) => setForm({ ...form, local_protected_subnet: e.target.value })} placeholder="172.16.1.2/32" />
                </div>
                <div>
                  <label htmlFor="localPort">HL7 port</label>
                  <input id="localPort" type="number" value={form.local_port} onChange={(e) => setForm({ ...form, local_port: Number(e.target.value), remote_port: Number(e.target.value) })} />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex items-center gap-3 rounded-xl bg-white p-3">
                  <input type="checkbox" checked={form.alert_when_down} onChange={(e) => setForm({ ...form, alert_when_down: e.target.checked })} style={{ width: 'auto' }} />
                  <span className="text-sm text-gray-700">Alert/mark attention when this tunnel is down</span>
                </label>
                <div>
                  <label htmlFor="expectedWindow">Expected online window</label>
                  <select id="expectedWindow" value={form.expected_online_window} onChange={(e) => setForm({ ...form, expected_online_window: e.target.value })}>
                    <option value="always">24/7</option>
                    <option value="business_hours">Business hours</option>
                    <option value="manual">Manual / as needed</option>
                  </select>
                </div>
              </div>
            </details>

            <button disabled={saving} onClick={saveConnection} className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              <Save className="mr-2 h-4 w-4" /> {saving ? 'Saving...' : form.id ? 'Update VPN connection' : 'Create VPN connection'}
            </button>
          </div>
        </div>

        <div className="space-y-6 xl:col-span-2">
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
            <h2 className="flex items-center text-lg font-semibold text-gray-900"><Route className="mr-2 h-5 w-5 text-blue-600" /> Customer setup sheet</h2>
            <p className="mt-1 text-sm text-gray-600">Give these values to the customer or copy them into their firewall.</p>
            <div className="mt-4 space-y-2">
              {customerInstructions.map((line) => (
                <div key={line} className="rounded-xl bg-gray-50 p-3 text-sm text-gray-800">{line}</div>
              ))}
            </div>
            <button onClick={() => copy(customerInstructions.join('\n'), 'Customer setup copied')} className="mt-4 inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Copy className="mr-2 h-4 w-4" /> Copy customer setup
            </button>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
            <div className="flex gap-3">
              <HelpCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold">Simple rule for users</h3>
                <p className="mt-1 text-sm">The customer never sends to public tcp/6661. They send HL7 through the tunnel to <span className="font-mono">{protectedIpOnly}:{form.local_port || 6661}</span>. The VPS forwards it privately to OIE/Mirth.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center text-xl font-semibold text-gray-900"><Wifi className="mr-2 h-5 w-5 text-blue-600" /> Connections, setup status & debug</h2>
            <p className="mt-1 text-sm text-gray-600">Plain-English next steps, health checks, live StrongSwan snippets, and filtered logs for each customer.</p>
          </div>
          <button onClick={load} className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh diagnostics
          </button>
        </div>

        {connections.length === 0 ? (
          <div className="mt-5 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-sm text-gray-600">
            No saved customer VPNs yet. Create the first one above; diagnostics will appear here after saving.
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
            {connections.map((connection) => {
              const diagnostic = diagnostics[connection.id];
              const summary = diagnostic?.summary;
              const statusLabel = summary?.status_label || (summary?.overall_ok ? 'READY' : summary?.established ? 'TUNNEL UP' : summary?.connecting ? 'CONNECTING' : 'ATTENTION');
              const availabilityMode = connection.availability_mode || (connection.auto_start === false ? 'customer_initiated' : 'always_on');
              const statusClass = statusLabel === 'READY' ? 'bg-green-100 text-green-800' : statusLabel === 'TUNNEL UP' ? 'bg-blue-100 text-blue-800' : statusLabel === 'CONNECTING' ? 'bg-amber-100 text-amber-800' : statusLabel === 'STANDBY' ? 'bg-slate-100 text-slate-800' : 'bg-red-100 text-red-800';
              return (
                <div key={connection.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{connection.site_label || connection.name}</h3>
                      <p className="mt-1 text-sm text-gray-600">{connection.peer_address || 'peer not set'} • {connection.remote_subnet || 'subnet not set'} • {connection.device_profile || 'profile not set'}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                      {statusLabel}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl bg-white p-3"><div className="text-gray-500">Tunnel</div><div className="font-semibold text-gray-900">{summary?.established ? 'Established' : summary?.connecting ? 'Connecting' : 'Not up'}</div></div>
                    <div className="rounded-xl bg-white p-3"><div className="text-gray-500">HL7 route</div><div className="font-semibold text-gray-900">{summary?.route_ready ? 'Ready' : 'Blocked'}</div></div>
                    <div className="rounded-xl bg-white p-3"><div className="text-gray-500">Availability</div><div className="text-xs font-semibold text-gray-900">{availabilityLabels[availabilityMode]?.title || availabilityMode}</div></div>
                    <div className="rounded-xl bg-white p-3"><div className="text-gray-500">Customer sends to</div><div className="font-mono text-xs text-gray-900">{(connection.local_protected_subnet || protectedDefault).split('/')[0]}:{connection.local_port}</div></div>
                    <div className="rounded-xl bg-white p-3"><div className="text-gray-500">Proposal</div><div className="text-xs font-semibold text-gray-900">{proposalLabels[connection.proposal_profile || 'unifi-auto-compatible'] || connection.proposal_profile}</div></div>
                  </div>

                  {diagnostic?.next_steps && diagnostic.next_steps.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {diagnostic.next_steps.map((step, idx) => (
                        <div key={`${step.title}-${idx}`} className={`rounded-xl p-3 text-sm ${step.level === 'success' ? 'bg-green-50 text-green-900' : step.level === 'warning' ? 'bg-amber-50 text-amber-900' : 'bg-blue-50 text-blue-900'}`}>
                          <div className="font-semibold">{step.title}</div>
                          <div className="mt-1">{step.detail}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={() => setForm(formFromConnection(connection))} className="inline-flex items-center rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50"><Pencil className="mr-2 h-4 w-4" /> Edit</button>
                    <button onClick={() => startConnection(connection)} className="inline-flex items-center rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"><PlayCircle className="mr-2 h-4 w-4" /> Start</button>
                    <button onClick={() => stopConnection(connection)} className="inline-flex items-center rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50"><StopCircle className="mr-2 h-4 w-4" /> Stop</button>
                    <button onClick={() => deleteConnection(connection)} className="inline-flex items-center rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"><Trash2 className="mr-2 h-4 w-4" /> Delete</button>
                    <button onClick={() => setDebugOpen({ ...debugOpen, [connection.id]: !debugOpen[connection.id] })} className="inline-flex items-center rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"><TerminalSquare className="mr-2 h-4 w-4" /> Debug</button>
                  </div>

                  {diagnostic && (
                    <div className="mt-4 space-y-2">
                      {diagnostic.checks.map((check) => (
                        <div key={check.name} className="flex items-start justify-between gap-3 rounded-xl bg-white p-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{check.name}</div>
                            <div className="text-xs text-gray-500">{check.detail}</div>
                          </div>
                          <span className={`rounded-full px-2 py-1 text-xs font-semibold ${check.ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{check.ok ? 'PASS' : 'FAIL'}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {debugOpen[connection.id] && diagnostic && (
                    <div className="mt-4 space-y-3">
                      <div className="rounded-xl bg-slate-950 p-3 text-slate-200">
                        <div className="mb-2 flex items-center justify-between">
                          <h4 className="flex items-center text-sm font-semibold"><Activity className="mr-2 h-4 w-4" /> StrongSwan status</h4>
                          <button onClick={() => copy(diagnostic.ipsec_statusall_excerpt || diagnostic.ipsec_status || '', 'Status copied')} className="text-xs text-slate-300 hover:text-white">Copy</button>
                        </div>
                        <pre className="max-h-56 overflow-auto whitespace-pre-wrap text-xs leading-5 text-slate-300">{diagnostic.ipsec_statusall_excerpt || diagnostic.ipsec_status || 'No status output.'}</pre>
                      </div>
                      <div className="rounded-xl bg-slate-950 p-3 text-slate-200">
                        <div className="mb-2 flex items-center justify-between">
                          <h4 className="flex items-center text-sm font-semibold"><TerminalSquare className="mr-2 h-4 w-4" /> Recent filtered logs</h4>
                          <button onClick={() => copy((diagnostic.recent_logs || []).join('\n'), 'Logs copied')} className="text-xs text-slate-300 hover:text-white">Copy</button>
                        </div>
                        <pre className="max-h-72 overflow-auto whitespace-pre-wrap text-xs leading-5 text-slate-300">{(diagnostic.recent_logs || []).join('\n') || 'No recent matching log lines yet. Ask the customer to save/enable the tunnel, then refresh.'}</pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl bg-slate-950 p-5 text-slate-100 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center font-semibold"><FileText className="mr-2 h-5 w-5" /> VPS config preview</h2>
            <button onClick={() => copy(generatedConfig, 'VPS config copied')} className="rounded-lg bg-white/10 p-2 hover:bg-white/20" title="Copy VPS config"><Copy className="h-4 w-4" /></button>
          </div>
          <pre className="overflow-auto whitespace-pre-wrap text-xs leading-5 text-slate-300">{generatedConfig}</pre>
        </div>

        <div className="rounded-2xl bg-slate-950 p-5 text-slate-100 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center font-semibold"><FileText className="mr-2 h-5 w-5" /> Secret line preview</h2>
            <button onClick={() => copy(generatedSecret, 'Secret line copied')} className="rounded-lg bg-white/10 p-2 hover:bg-white/20" title="Copy secrets line"><Copy className="h-4 w-4" /></button>
          </div>
          <pre className="overflow-auto whitespace-pre-wrap text-xs leading-5 text-slate-300">{generatedSecret}</pre>
          <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-100">
            Do not paste real PSKs into chat. The saved PSK is write-only and redacted from diagnostics/logs.
          </div>
        </div>
      </div>
    </div>
  );
}
